import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { SectionHeader } from "./Reveal";
import { INFRA_SERVICIOS } from "../../data/site";
import { EASE } from "../../lib/motion";

export default function InfraCarousel() {
  const trackRef = useRef(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);

  const update = () => {
    const el = trackRef.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 8);
    setCanNext(el.scrollLeft < el.scrollWidth - el.clientWidth - 8);
  };

  useEffect(() => {
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  const scroll = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.75, behavior: "smooth" });
  };

  const arrowCls = (enabled) =>
    `flex h-11 w-11 items-center justify-center rounded-full border transition-all duration-300 ${
      enabled
        ? "border-border bg-card text-foreground hover:border-electric/50 hover:text-electric"
        : "border-border bg-card/40 text-muted-foreground/40"
    }`;

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-6">
        <SectionHeader
          eyebrow="Servicios técnicos"
          title="Infraestructura tecnológica, instalada por expertos."
          description="Además de software, instalamos y acompañamos la infraestructura física que mantiene tu operación conectada, protegida y funcionando."
        />
        <div className="flex gap-3">
          <button
            onClick={() => scroll(-1)}
            disabled={!canPrev}
            aria-label="Anterior servicio"
            data-testid="infra-carousel-prev-button"
            className={arrowCls(canPrev)}
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            onClick={() => scroll(1)}
            disabled={!canNext}
            aria-label="Siguiente servicio"
            data-testid="infra-carousel-next-button"
            className={arrowCls(canNext)}
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      </div>

      <div
        ref={trackRef}
        onScroll={update}
        className="no-scrollbar mt-12 flex snap-x snap-mandatory gap-5 overflow-x-auto pb-2"
        data-testid="infra-carousel-track"
      >
        {INFRA_SERVICIOS.map((s, i) => (
          <motion.article
            key={s.title}
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ delay: i * 0.07, duration: 0.65, ease: EASE }}
            className="card-lift group w-[290px] shrink-0 snap-start overflow-hidden rounded-2xl border border-border bg-card hover:border-electric/40 hover:shadow-card-hover sm:w-[340px]"
            data-testid={`infra-card-${i}`}
          >
            <div className="relative h-44 overflow-hidden">
              <img
                src={s.img}
                alt={s.title}
                loading="lazy"
                className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#070B12]/75 via-[#070B12]/10 to-transparent" aria-hidden="true" />
              <span className="absolute left-4 top-4 flex h-9 w-9 items-center justify-center rounded-xl border border-white/20 bg-white/10 text-white backdrop-blur">
                <s.icon className="h-4.5 w-4.5 h-[18px] w-[18px]" strokeWidth={1.8} />
              </span>
            </div>
            <div className="p-6">
              <h3 className="font-display text-lg font-semibold leading-snug">{s.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.desc}</p>
            </div>
          </motion.article>
        ))}
      </div>
    </div>
  );
}
